import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

/**
 * Custom hook for managing Student Fee Assignments (Combined View)
 *
 * Fetches and combines ALL finance-related data for a student:
 * - School fees (from fee structures - StudentFeeAssignment)
 * - Custom fees (approved custom fee requests)
 * - Transport fees (TransportFeeAssignment)
 * - BOT clearance fees (BotClearanceAssignment)
 *
 * Provides a complete financial overview for a student
 */
const useStudentFees = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [studentFinances, setStudentFinances] = useState(null);

  /**
   * Fetch combined student fees from all sources using optimized single endpoint
   * @param {string} spc - Student Payment Code
   */
  const fetchStudentCompleteFinances = useCallback(
    async (spc) => {
      if (!spc) {
        message.error("Student SPC is required");
        return null;
      }

      setLoading(true);
      try {
        // Use the optimized single endpoint that returns all financial data
        const url = domainSettings.urls.finance.studentCompleteFinances.replace(
          ":spc",
          spc,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (!response.ok) {
          message.error(response.msg || "Failed to fetch student finances");
          setStudentFinances(null);
          return null;
        }

        const data = response.data;

        // Map the backend response to our frontend data structure
        const combinedData = {
          spc,
          student: data.student,
          schoolFees: {
            items: data.schoolFees?.items || [],
            totalDue: parseFloat(data.schoolFees?.totalDue) || 0,
            totalPaid: parseFloat(data.schoolFees?.totalPaid) || 0,
            balance: parseFloat(data.schoolFees?.balance) || 0,
            count: data.schoolFees?.count || 0,
          },
          customFees: {
            items: data.customFees?.items || [],
            totalDue: parseFloat(data.customFees?.totalDue) || 0,
            totalPaid: parseFloat(data.customFees?.totalPaid) || 0,
            balance: parseFloat(data.customFees?.balance) || 0,
            count: data.customFees?.count || 0,
          },
          transportFees: {
            items: data.transportFees?.items || [],
            totalDue: parseFloat(data.transportFees?.totalDue) || 0,
            totalPaid: parseFloat(data.transportFees?.totalPaid) || 0,
            balance: parseFloat(data.transportFees?.balance) || 0,
            count: data.transportFees?.count || 0,
          },
          botClearances: {
            items: data.botFees?.items || [],
            totalDue: parseFloat(data.botFees?.totalDue) || 0,
            totalPaid: parseFloat(data.botFees?.totalPaid) || 0,
            balance: parseFloat(data.botFees?.balance) || 0,
            count: data.botFees?.count || 0,
          },
          grandTotal: {
            totalDue: parseFloat(data.grandTotal?.totalDue) || 0,
            totalPaid: parseFloat(data.grandTotal?.totalPaid) || 0,
            balance: parseFloat(data.grandTotal?.balance) || 0,
          },
          currentTermTotal: data.currentTermTotal
            ? {
                totalDue: parseFloat(data.currentTermTotal.totalDue) || 0,
                totalPaid: parseFloat(data.currentTermTotal.totalPaid) || 0,
                balance: parseFloat(data.currentTermTotal.balance) || 0,
              }
            : null,
          creditWallet: {
            balance: parseFloat(data.creditWallet?.balance) || 0,
            heldBalance: parseFloat(data.creditWallet?.heldBalance) || 0,
            entries: data.creditWallet?.entries || [],
          },
          financialPosition: {
            balanceBroughtForward:
              parseFloat(data.financialPosition?.balanceBroughtForward) || 0,
            prepaymentCredit:
              parseFloat(data.financialPosition?.prepaymentCredit) || 0,
            grossOutstanding:
              parseFloat(data.financialPosition?.grossOutstanding) || 0,
            currentOutstanding:
              parseFloat(data.financialPosition?.currentOutstanding) || 0,
            netOutstanding:
              parseFloat(data.financialPosition?.netOutstanding) || 0,
            netCredit: parseFloat(data.financialPosition?.netCredit) || 0,
            currentTermOutstanding:
              parseFloat(data.financialPosition?.currentTermOutstanding) || 0,
            currentTermPayable:
              parseFloat(data.financialPosition?.currentTermPayable) || 0,
            currentTerm: data.financialPosition?.currentTerm || null,
            statementLines: data.financialPosition?.statementLines || [],
          },
          payments: data.payments || [],
          lastUpdated: new Date().toISOString(),
        };

        setStudentFinances(combinedData);
        return combinedData;
      } catch (error) {
        console.error("Error fetching complete student finances:", error);
        message.error(
          "An error occurred while fetching student financial data",
        );
        setStudentFinances(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /** Set a student-specific tuition amount with a required audit reason. */
  const overrideStudentFeeAmount = useCallback(
    async (assignmentId, amount, reason) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.finance.studentFeeAmountOverride.replace(
            ":id",
            assignmentId,
          );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          { amount, reason },
          navigate,
        );
        if (response.ok) {
          message.success("Student tuition amount updated");
          return response.data;
        }
        Utilities.showError(
          response,
          "Failed to update student tuition amount",
        );
        return null;
      } catch (error) {
        console.error("Error overriding student tuition amount:", error);
        message.error("An error occurred while updating the tuition amount");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchFeeAdjustmentHistory = useCallback(
    async (assignmentId) => {
      try {
        const url =
          domainSettings.urls.finance.studentFeeAdjustmentHistory.replace(
            ":id",
            assignmentId,
          );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) return response.data || [];
        Utilities.showError(
          response,
          "Failed to fetch tuition adjustment history",
        );
        return [];
      } catch (error) {
        console.error("Error fetching tuition adjustment history:", error);
        message.error("An error occurred while fetching adjustment history");
        return [];
      }
    },
    [navigate],
  );

  /**
   * Fetch only school fees (StudentFeeAssignment)
   * @param {string} spc - Student Payment Code
   * @param {Object} filters - Optional filters (term_id, acadyr, status)
   */
  const fetchStudentSchoolFees = useCallback(
    async (spc, filters = {}) => {
      setLoading(true);
      try {
        const queryString = new URLSearchParams(filters).toString();
        const baseUrl = domainSettings.urls.finance.studentFees.replace(
          ":spc",
          spc,
        );
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch student fees");
          return null;
        }
      } catch (error) {
        console.error("Error fetching student school fees:", error);
        message.error("An error occurred while fetching student fees");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Fetch student fee summary
   * @param {string} spc - Student Payment Code
   */
  const fetchStudentFeeSummary = useCallback(
    async (spc) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.studentFeeSummary.replace(
          ":spc",
          spc,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch fee summary");
          return null;
        }
      } catch (error) {
        console.error("Error fetching fee summary:", error);
        message.error("An error occurred while fetching fee summary");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Clear cached student finances
   */
  const clearStudentFinances = useCallback(() => {
    setStudentFinances(null);
  }, []);

  /**
   * Apply a student's credit wallet to outstanding fees.
   * @param {string} spc - Student Payment Code
   * @param {number} [assignmentId] - Optional fee assignment to target; omit to
   *   apply across all outstanding fees oldest-term-first.
   */
  const applyStudentCredit = useCallback(
    async (spc, assignmentId = null) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.applyStudentCredit.replace(
          ":spc",
          spc,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          assignmentId ? { assignmentId } : {},
          navigate,
        );

        if (response.ok) {
          message.success(response.data?.detail || "Credit applied");
          return response.data;
        }
        Utilities.showError(response, "Failed to apply credit");
        return null;
      } catch (error) {
        console.error("Error applying student credit:", error);
        message.error("An error occurred while applying credit");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Apply discount to a school fee assignment
   * @param {number} assignmentId - Fee assignment ID
   * @param {number} discountPercentage - Discount percentage (0-100)
   * @param {string} reason - Optional reason for discount
   */
  const applySchoolFeeDiscount = useCallback(
    async (assignmentId, discountPercentage, reason = "") => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.studentFeeDiscount.replace(
          ":id",
          assignmentId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {
            discountPercentage,
            reason,
          },
          navigate,
        );

        if (response.ok) {
          message.success("Discount applied successfully");
          return response.data;
        } else {
          Utilities.showError(response, "Failed to apply discount");
          return null;
        }
      } catch (error) {
        console.error("Error applying school fee discount:", error);
        message.error("An error occurred while applying discount");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Remove discount from a school fee assignment
   * @param {number} assignmentId - Fee assignment ID
   */
  const removeSchoolFeeDiscount = useCallback(
    async (assignmentId) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.studentFeeDiscount.replace(
          ":id",
          assignmentId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Discount removed successfully");
          return response.data;
        } else {
          Utilities.showError(response, "Failed to remove discount");
          return null;
        }
      } catch (error) {
        console.error("Error removing school fee discount:", error);
        message.error("An error occurred while removing discount");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Apply discount to a transport fee assignment
   * @param {number} assignmentId - Transport fee assignment ID
   * @param {number} discountPercentage - Discount percentage (0-100)
   * @param {string} reason - Optional reason for discount
   */
  const applyTransportFeeDiscount = useCallback(
    async (assignmentId, discountPercentage, reason = "") => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.transportFeeDiscount.replace(
          ":id",
          assignmentId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {
            discountPercentage,
            reason,
          },
          navigate,
        );

        if (response.ok) {
          message.success("Transport fee discount applied successfully");
          return response.data;
        } else {
          Utilities.showError(
            response,
            "Failed to apply transport fee discount",
          );
          return null;
        }
      } catch (error) {
        console.error("Error applying transport fee discount:", error);
        message.error(
          "An error occurred while applying transport fee discount",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Remove discount from a transport fee assignment
   * @param {number} assignmentId - Transport fee assignment ID
   */
  const removeTransportFeeDiscount = useCallback(
    async (assignmentId) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.transportFeeDiscount.replace(
          ":id",
          assignmentId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Transport fee discount removed successfully");
          return response.data;
        } else {
          Utilities.showError(
            response,
            "Failed to remove transport fee discount",
          );
          return null;
        }
      } catch (error) {
        console.error("Error removing transport fee discount:", error);
        message.error(
          "An error occurred while removing transport fee discount",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Apply discount to a BOT clearance assignment
   * @param {number} assignmentId - BOT clearance assignment ID
   * @param {number} discountPercentage - Discount percentage (0-100)
   * @param {string} reason - Optional reason for discount
   */
  const applyBotDiscount = useCallback(
    async (assignmentId, discountPercentage, reason = "") => {
      setLoading(true);
      try {
        const url = domainSettings.urls.bot.clearanceDiscount.replace(
          ":id",
          assignmentId,
        );
        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          {
            discountPercentage,
            reason,
          },
          navigate,
        );

        if (response.ok) {
          message.success("BOT discount applied successfully");
          return response.data;
        } else {
          Utilities.showError(response, "Failed to apply BOT discount");
          return null;
        }
      } catch (error) {
        console.error("Error applying BOT discount:", error);
        message.error("An error occurred while applying BOT discount");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Remove discount from a BOT clearance assignment
   * @param {number} assignmentId - BOT clearance assignment ID
   */
  const removeBotDiscount = useCallback(
    async (assignmentId) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.bot.clearanceDiscount.replace(
          ":id",
          assignmentId,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("BOT discount removed successfully");
          return response.data;
        } else {
          Utilities.showError(response, "Failed to remove BOT discount");
          return null;
        }
      } catch (error) {
        console.error("Error removing BOT discount:", error);
        message.error("An error occurred while removing BOT discount");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  /**
   * Initiate payment for a custom fee
   * @param {string} cfpc - Custom Fee Payment Code
   * @param {number} amount - Optional amount (defaults to balance)
   * @param {string} phoneNumber - Customer mobile money number (required for SurePay MoMo push)
   * @returns {object} - Payment initiation response
   */
  const initiateCustomFeePayment = useCallback(
    async (cfpc, amount = null, phoneNumber = null) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.finance.initiatePayment;
        const payload = {
          paymentCode: cfpc,
          codeType: "CFPC",
          description: "Custom Fee Payment",
        };

        if (amount) payload.amount = amount;
        if (phoneNumber) payload.phoneNumber = phoneNumber;

        const response = await Utilities.fetchWithAuth(
          "POST",
          url,
          payload,
          navigate,
        );

        if (response.ok) {
          return response.data;
        } else {
          Utilities.showError(response, "Failed to initiate payment");
          return null;
        }
      } catch (error) {
        console.error("Error initiating custom fee payment:", error);
        message.error("An error occurred while initiating payment");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return {
    // State
    loading,
    studentFinances,

    // Methods
    fetchStudentCompleteFinances,
    fetchStudentSchoolFees,
    fetchStudentFeeSummary,
    clearStudentFinances,
    applyStudentCredit,
    overrideStudentFeeAmount,
    fetchFeeAdjustmentHistory,
    // Discount methods
    applySchoolFeeDiscount,
    removeSchoolFeeDiscount,
    applyTransportFeeDiscount,
    removeTransportFeeDiscount,
    applyBotDiscount,
    removeBotDiscount,
    // Payment methods
    initiateCustomFeePayment,
  };
};

export default useStudentFees;
